/**
 * The invite link someone arrived with (`/?link=<token>&via=<channel>`). It has
 * to outlive signing up and claiming a handle — a reload or two — so it waits
 * in sessionStorage until the signed-in app opens it. The channel tag (`via`,
 * slice 3) waits beside it, for referral attribution in slice 4.
 */

import { isVia, readInviteParams, type Via } from '../onboarding/share';
import { track } from '../onboarding/track';

const KEY = 'toiyp.inviteLink';
const VIA_KEY = 'toiyp.inviteLinkVia';

/** Move `?link=` (and `via=`) off the URL into storage; return whatever is pending. */
export function takeInviteLinkFromUrl(): string | null {
  const { token, via } = readInviteParams(window.location.search);
  if (token) {
    const params = new URLSearchParams(window.location.search);
    params.delete('link');
    params.delete('via');
    const rest = params.toString();
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash,
    );
    track({ event: 'invite_link_opened', via });
    try {
      sessionStorage.setItem(KEY, token);
      if (via) sessionStorage.setItem(VIA_KEY, via);
      else sessionStorage.removeItem(VIA_KEY);
    } catch {
      // Private mode — the token still lives for this page load.
      return token;
    }
  }
  return pendingInviteLink() ?? token;
}

export function pendingInviteLink(): string | null {
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

/** Which channel the pending link came by, if it was tagged. */
export function pendingInviteVia(): Via | null {
  try {
    const via = sessionStorage.getItem(VIA_KEY);
    return isVia(via) ? via : null;
  } catch {
    return null;
  }
}

export function clearPendingInviteLink(): void {
  try {
    sessionStorage.removeItem(KEY);
    sessionStorage.removeItem(VIA_KEY);
  } catch {
    // nothing to clear
  }
}
